import {SelectionModel} from '@angular/cdk/collections';
import {FlatTreeControl} from '@angular/cdk/tree';
import {Input, OnInit, Directive, OnChanges, ElementRef, ViewChild, AfterViewChecked, SimpleChanges} from '@angular/core';
import {MatTreeFlatDataSource, MatTreeFlattener, MatTreeNode} from '@angular/material/tree';
import {ApiService} from '../../services/api.service';
import {DynamicImageSet} from '../../model/dynamic-image-set';
import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {TestArea} from '../test-area';
import {ErrorHelper} from '../ErrorHelper';
import {NotifierService} from 'angular-notifier';

/**
 * Node for item
 */
export class TreeNode {
  children: TreeNode[];
  item: string;
  description: string;
  isSelected: boolean;
  isDisabled: boolean;
  isNone = false;
}

/** Flat node with expandable and level information */
export class TreeFlatNode {
  item: string;
  level: number;
  expandable: boolean;
  description: string;
  isSelected: boolean;
  isDisabled: boolean;
  isNone = false;
}

@Directive()
export abstract class TreeComponent implements OnInit, OnChanges {

  @Input() dynamicImageSet: DynamicImageSet;
  @Input() id: string;
  @Input() version: string;
  @Input() viewport: string;
  @Input() testArea: TestArea;

  @ViewChild('myIdentifier') myIdentifier: ElementRef;

  preselectedLayerNodes = [];
  target: any;

  rowHeight: any;
  containerWidth: any;

  selectedNodes = [];

  images: any[];
  angles: string[];
  areImagesLoaded: boolean;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TreeFlatNode, TreeNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<TreeNode, TreeFlatNode>();

  treeControl: FlatTreeControl<TreeFlatNode>;

  treeFlattener: MatTreeFlattener<TreeNode, TreeFlatNode>;

  dataSource: MatTreeFlatDataSource<TreeNode, TreeFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<TreeFlatNode>(true /* multiple */);

  dataChange = new BehaviorSubject<TreeNode[]>([]);

  getLevel = (node: TreeFlatNode) => node.level;

  isExpandable = (node: TreeFlatNode) => node.expandable;

  getChildren = (node: TreeNode): TreeNode[] => node.children;

  hasChild = (_: number, nodeData: TreeFlatNode) => nodeData.expandable;

  hasNoContent = (_: number, nodeData: TreeFlatNode) => nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: TreeNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
      ? existingNode
      : new TreeFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.description = node.description;
    flatNode.expandable = !!node.children?.length;
    flatNode.isDisabled = node.isDisabled;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    this.treeControl.dataNodes.push(flatNode);
    if (this.preselectedLayerNodes.includes(node.item) || node.isSelected) {
      this.checklistSelection.select(flatNode);
    }
    return flatNode;
  }

  constructor(public apiService: ApiService, public notifierService: NotifierService) {
    this.treeControl = new FlatTreeControl<TreeFlatNode>(this.getLevel, this.isExpandable);
    this.treeControl.dataNodes = [];
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
      this.isExpandable, this.getChildren);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.dataChange.subscribe(data => {
      this.dataSource.data = data;
    });
  }

  initialize(): void {
    this.images = [];
    this.angles = this.dynamicImageSet.previewAngles;
    this.preselectedLayerNodes = [];
    this.treeControl.dataNodes = [];
    this.nestedNodeMap.clear();
    this.flatNodeMap.clear();
    this.dataSource.data = [];
    this.selectedNodes = [];
    this.checklistSelection.clear();
    this.target = '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    // overwritten in subclasses
  }

  ngOnInit(): void {
  }

  setRowHeight(): void {
    this.containerWidth = this.myIdentifier.nativeElement.offsetWidth - 49;
    console.log('Width:' + this.containerWidth);
    if (this.containerWidth < this.dynamicImageSet.width) {
      this.rowHeight = ((this.containerWidth) * (this.dynamicImageSet.height) / this.dynamicImageSet.width) + 45;
    } else {
      this.rowHeight = this.dynamicImageSet.height + 45;
    }
  }

  onResize(event): void {
    this.setRowHeight();
  }

  async loadOnlyImagesAsync(): Promise<any> {
    try {
      await this.loadImages();
    } catch (error) {
      console.log(error);
    }
  }


  async loadDataAsync(): Promise<any> {
    try {
      await this.loadPreselectedNodes();
      await this.loadTreeData();
      await this.selectNoneLayerAsDefault();
      await this.loadTargetNames();
      await this.loadImages();
      await this.expandTree();
    } catch (error) {
      console.log(error);
    }
  }

  abstract loadPreselectedNodes(): Promise<boolean>;

  abstract loadTreeData(): Promise<boolean>;

  abstract buildTree(obj: { [key: string]: any }, level: number): TreeNode[];

  abstract getImage(dynamicImageSet: DynamicImageSet, selectedNodes: string[], view: string): Observable<Blob>;

  selectNoneLayerAsDefault(): Promise<boolean> {
    let parentNodes: TreeFlatNode[] = Array.from(this.nestedNodeMap.values());
    parentNodes = parentNodes.filter(layerFlatNode => layerFlatNode.level === 0 || layerFlatNode.level === 1);
    let noChildrenSelected: boolean;
    parentNodes.forEach(parentNode => {
      const children = this.treeControl.getDescendants(parentNode);
      noChildrenSelected = true;
      for (const node of children) {
        if (this.checklistSelection.selected.filter(n => !!n).filter(n => n.item !== 'none').some(n => n.item === node.item) === true) {
          noChildrenSelected = false;
        }
      }
      if (noChildrenSelected === true && Array.isArray(children) && children.length > 0) {
        const noneNode = children.filter(n => !!n).find(node => node.item === 'none');
        this.checklistSelection.select(noneNode);
      }
    });
    console.log('None options selected as default');
    return Promise.resolve(true);
  }

  loadTargetNames(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getTargetNames(this.dynamicImageSet, this.testArea).toPromise()
        .then(
          (data) => {
            this.target = data;
            console.log('Fetched the target names');
            resolve(true);
          },
          (error) => {
            const errorMessage = ErrorHelper.disectError('load target names', error);
            this.notifierService.notify('error', errorMessage);
            resolve(false);
          });
    });
  }

  loadImages(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.selectedNodes = this.checklistSelection.selected
        .filter(node => !!node)
        .filter(node => node.item !== 'none')
        .map(node => node.item);
      if (Array.isArray(this.selectedNodes) && this.selectedNodes.length === 0) {
        this.selectedNodes = this.preselectedLayerNodes;
      }

      const imageCalls = new Array<Observable<Blob>>();
      for (const view of this.dynamicImageSet.previewAngles.filter(String)) {
        const imageCall = this.getImage(this.dynamicImageSet, this.selectedNodes, view);
        imageCalls.push(imageCall);
      }
      const loadedImages = [];

      forkJoin(imageCalls).subscribe(data => {
        data.forEach((smallData, index) => {
          this.createImageFromBlob(smallData, loadedImages, index);
          console.log('Fetched ' + index + ' images');
        });
      }, error => {
        const errorMessage = ErrorHelper.disectError('load image', error);
        this.areImagesLoaded = false;
        this.images = [];
        this.notifierService.notify('error', errorMessage);
        reject(false);
      }, () => {
        this.images = [];
        this.areImagesLoaded = true;
        this.images = loadedImages;
        this.setRowHeight();
        resolve(true);
      });
    });
  }

  expandTree(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const promise = Promise.resolve(true);
      try {
        this.treeControl.expandAll();
      } catch (error) {
        console.log(error);
        reject(error);
      }
      console.log('Tree expanded');
      return promise;
    });
  }

  layerSelectionToggle(node: TreeFlatNode): void {
    const siblings = this.treeControl.getDescendants(this.getParentNode(node));
    for (const sibling of siblings) {
      this.checklistSelection.deselect(sibling);
    }
    this.checklistSelection.toggle(node);

    this.loadImages();
  }

  isDisabled(node: TreeFlatNode): boolean {
    return node.isDisabled;
  }

  getParentNode(node: TreeFlatNode): TreeFlatNode | null {
    const currentLevel = this.getLevel(node);
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  createImageFromBlob(image: Blob, images: any[], index: number): void {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      images[index] = reader.result;
    }, false);
    if (image) {
      reader.readAsDataURL(image);
    }
  }

}
